import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "utility-functions"
    }}>{`Utility Functions`}</h1>
    <h2 {...{
      "id": "usedebouncedstate"
    }}><strong parentName="h2">{`useDebouncedState`}</strong></h2>
    <p>{`This is a slight variation of React's useState hook.`}</p>
    <pre><code parentName="pre" {...{}}>{`const [state, debouncedState, setState] = useDebouncedState(initialState, wait);
`}</code></pre>
    <h3 {...{
      "id": "arguments"
    }}><strong parentName="h3">{`Arguments:`}</strong></h3>
    <h3 {...{
      "id": "initialstate-any"
    }}><inlineCode parentName="h3">{`initialState`}</inlineCode>{`: any`}</h3>
    <p>{`Same as the initialState of the `}<inlineCode parentName="p">{`useState`}</inlineCode>{` hook`}</p>
    <h3 {...{
      "id": "wait-number-default--100"
    }}><inlineCode parentName="h3">{`wait`}</inlineCode>{`: number (default = 100)`}</h3>
    <p>{`The debounce wait value in milliseconds.`}</p>
    <h3 {...{
      "id": "return-values-array"
    }}><strong parentName="h3">{`Return Values Array:`}</strong></h3>
    <h3 {...{
      "id": "state-any"
    }}><inlineCode parentName="h3">{`state`}</inlineCode>{`: any`}</h3>
    <p>{`Same as the `}<inlineCode parentName="p">{`state`}</inlineCode>{` from the `}<inlineCode parentName="p">{`useState`}</inlineCode>{` hook`}</p>
    <h3 {...{
      "id": "debouncedstate-any"
    }}><inlineCode parentName="h3">{`debouncedState`}</inlineCode>{`: any`}</h3>
    <p>{`Similar to state, except, this will be updated at most once per every `}<inlineCode parentName="p">{`wait`}</inlineCode>{` milliseconds.`}</p>
    <p>{`For example, if you call `}<inlineCode parentName="p">{`setState`}</inlineCode>{`, 3 times(with values 'foo', 'bar', and 'baz') within 50 milliseconds,`}</p>
    <ul>
      <li parentName="ul">{`state will get all three values: 'foo', 'bar', and 'baz'`}</li>
      <li parentName="ul">{`debouncedState will get only `}<inlineCode parentName="li">{`baz`}</inlineCode></li>
    </ul>
    <h3 {...{
      "id": "setstate-function"
    }}><inlineCode parentName="h3">{`setState`}</inlineCode>{`: Function`}</h3>
    <p>{`Same as the `}<inlineCode parentName="p">{`setState`}</inlineCode>{` from the `}<inlineCode parentName="p">{`useState`}</inlineCode>{` hook`}</p>
    <h2 {...{
      "id": "usefilter"
    }}><strong parentName="h2">{`useFilter`}</strong></h2>
    <p>{`This hook does two simple things`}</p>
    <ol>
      <li parentName="ol">{`Return the original data list when the `}<inlineCode parentName="li">{`filterText`}</inlineCode>{` is empty`}</li>
      <li parentName="ol">{`Memoize the search results for fast updates`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "harmony",
        "harmony": true
      }}>{`const filteredData = useFilter(filterFn, data, filterText);
`}</code></pre>
    <p>{`as shown above, it accepts 3 arguments,`}</p>
    <h3 {...{
      "id": "filterfn-function"
    }}><inlineCode parentName="h3">{`filterFn`}</inlineCode>{`: Function`}</h3>
    <p>{`The filter function should take the following form,`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "harmony",
        "harmony": true
      }}>{`function filterFn(data, filterText) {
  // filter logic
  return filteredData;
}
`}</code></pre>
    <h3 {...{
      "id": "data-array"
    }}><inlineCode parentName="h3">{`data`}</inlineCode>{`: Array`}</h3>
    <p>{`Original data array to be filtered.`}</p>
    <h3 {...{
      "id": "filtertext-string"
    }}><inlineCode parentName="h3">{`filterText`}</inlineCode>{`: string`}</h3>
    <p>{`Search text to base the filtering on.`}</p>
    <p>{`The key idea here is that, the `}<inlineCode parentName="p">{`filterFn`}</inlineCode>{` function will be
called internally with the `}<inlineCode parentName="p">{`data`}</inlineCode>{` and `}<inlineCode parentName="p">{`filterText`}</inlineCode>{` search
string in an optimal manner. `}</p>
    <h2 {...{
      "id": "createfilter"
    }}><strong parentName="h2">{`createFilter`}</strong></h2>
    <p>{`Given a list of attributes, return a function which will filter data
based on those attributes.`}</p>
    <p>{`It takes the following form:`}</p>
    <pre><code parentName="pre" {...{}}>{`const filterFn = createFilter(attributes);
`}</code></pre>
    <h3 {...{
      "id": "attributes-string"
    }}><inlineCode parentName="h3">{`attributes`}</inlineCode>{`: string[]`}</h3>
    <p>{`Object keys to base the filtering on.`}</p>
    <h3 {...{
      "id": "filterfn-function-1"
    }}><inlineCode parentName="h3">{`filterFn`}</inlineCode>{`: Function`}</h3>
    <p>{`A function which can filter data given a search text.
More about this function can be found after the following example.`}</p>
    <p>{`Suppose we have a data set as follows,`}</p>
    <pre><code parentName="pre" {...{}}>{`const dataList = [
  { name: 'Paolo', profession: 'Software Engineer', company: 'hipages'},
  { name: 'Ayesha', profession: 'Flight Dispatcher', company: 'SL Airlines'},
  { name: 'Daham', profession: 'Tech Lead', company: 'Sysco Labs'},
  // ...
]
`}</code></pre>
    <p>{`And we create a filter function which can filter data based on `}<inlineCode parentName="p">{`name`}</inlineCode>{` and `}<inlineCode parentName="p">{`profession`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`const filterFn = createFilter(['name', 'profession']);
`}</code></pre>
    <p>{`Now if we supply the dataList to this function, along with some text,
we will get the following results`}</p>
    <pre><code parentName="pre" {...{}}>{`// Returns row #3
filterFn(dataList, 'Daham');

// Returns rows #1 and #2 since it matches: "Engineer" and "Dispatcher"
filterFn(dataList, 'er');

// Returns rows #1 and #2 since it matches: "Paolo" and "Dispatcher"
filterFn(dataList, 'pa');

// Doesn't yield any rows, as we didn't add \`company\` to the filter list
filterFn(dataList, 'Sysco Labs');
`}</code></pre>
    <h3 {...{
      "id": ""
    }}></h3>
    <p>{`The filter function returned from the createFilter function takes two arguments,
and returns an array of filtered data`}</p>
    <pre><code parentName="pre" {...{}}>{`const filteredData = filterFn(dataList, filterText);
`}</code></pre>
    <h3 {...{
      "id": "datalist-array"
    }}><inlineCode parentName="h3">{`dataList`}</inlineCode>{`: array`}</h3>
    <p>{`Original object array to be filtered.`}</p>
    <h3 {...{
      "id": "filtertext-string-1"
    }}><inlineCode parentName="h3">{`filterText`}</inlineCode>{`: string`}</h3>
    <p>{`Search text to base the filtering on.`}</p>
    <h3 {...{
      "id": "filtereddata-array"
    }}><inlineCode parentName="h3">{`filteredData`}</inlineCode>{`: array`}</h3>
    <p>{`Similar structure to `}<inlineCode parentName="p">{`dataList`}</inlineCode>{`, just with fewer entries.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      